import type {
  Order,
  OrderFullfillmentCenterReference,
  PricedArticleReference,
  StockKeepingUnit,
} from 'domain-model/types/oms';
import type {
  ProcurementArticleReference,
  Cart,
  CartRow,
  CartReference,
  CartSetCartRowParameters,
  Currency,
} from 'domain-model/types/pcs';

import type { MeCensoredCustomer } from 'domain-model/types/supplier-gateway';

import domainModel from 'domain-model';
import { getAxiosInstance } from '../utils/appWrapper';
import { ACTION_TYPES } from '../reducers/persisted-multi-cart.reducer';
import keyedDebounceSingleton from '../utils/keyed-debounce-singleton';

const _ = require('lodash');

_.mixin(require('lodash-uuid'));

interface Reference {
  id: string;
  [key: string]: string;
}

interface SessionUser {
  id: string;
  customerSubUnit: Reference;
  supplierCustomer: Reference;
  connectedSupplierCustomers: MeCensoredCustomer[];
  subdomain: string;
}

export interface Authentication {
  sessionUser: SessionUser;
}

const getPersistedCartTarget = (
  authentication: Authentication,
  orderFullfillmentCenter: OrderFullfillmentCenterReference,
  currency: Currency,
) => {
  const cartOrigin = domainModel.cart.getCartOrigin(
    authentication.sessionUser.subdomain,
  );

  const setCartRowTarget = {
    orderFullfillmentCenter: { id: orderFullfillmentCenter.id },
    cartOrigin,
    active: true,
    currency,
    cartState: 'building',
  };

  if (authentication.sessionUser.customerSubUnit) {
    // @ts-expect-error yeah I cannot be arsed to type this
    setCartRowTarget['$customerSubUnit.id$'] =
      authentication.sessionUser.customerSubUnit.id;
  } else {
    // @ts-expect-error yeah I cannot be arsed to type this
    setCartRowTarget['$supplierCustomer.id$'] =
      authentication.sessionUser.supplierCustomer.id;
  }
  return setCartRowTarget;
};

const persistCartRow = async (
  dispatch: Function,
  getState: Function,
  article: PricedArticleReference,
  quantity: number,
  procurementArticle?: ProcurementArticleReference,
  orderedSKU?: StockKeepingUnit,
) => {
  const { authentication } = getState();

  const cartOrigin = domainModel.cart.getCartOrigin(
    authentication.sessionUser.subdomain,
  );

  const payload = {
    target: getPersistedCartTarget(
      authentication,
      article.myOrderFullfillmentCenter as OrderFullfillmentCenterReference,
      article.currency as Currency,
    ),
    quantity,
    pricedArticle: article,
    orderUnit: article.orderUnit,
    orderFullfillmentCenter: article.myOrderFullfillmentCenter,
    cartOrigin,
    procurementArticle,
    orderedSKU,
  } as CartSetCartRowParameters;

  if (authentication.sessionUser.customerSubUnit) {
    payload.customerSubUnit = authentication.sessionUser.customerSubUnit;
  } else {
    payload.supplierCustomer = authentication.sessionUser.supplierCustomer;
  }

  await dispatch({
    type: ACTION_TYPES.PERSISTED_CART_ROW_UPDATED,
    payload,
  });

  keyedDebounceSingleton.debounce({
    queue: 'persistCartRow',
    key: procurementArticle?.id || _.toString(article.id),
    delayedFunction: () => {
      getAxiosInstance().post('actions', {
        payload,
        action: 'v1.cart.setCartRow',
      });
    },
    delay: 1000,
    retry: true,
  });
};

export const updateRow =
  (
    article: PricedArticleReference,
    quantity: number,
    procurementArticle?: ProcurementArticleReference,
  ) =>
  (dispatch: Function, getState: Function) => {
    persistCartRow(dispatch, getState, article, quantity, procurementArticle);
  };

export const writeAndPlaceOrder =
  (cart: Cart, requestedDeliveryDate: string, remarks: string) => async () => {
    const placedCart = await getAxiosInstance().post('actions', {
      payload: {
        id: cart.id,
        requestedDeliveryDate,
        remarks,
      },
      action: domainModel.cart.actions.writeAndPlaceOrder,
    });
    return placedCart;
  };

export const abandonCart = (cart: Cart) => async () => {
  await getAxiosInstance().post('actions', {
    payload: {
      id: cart.id,
    },
    action: domainModel.cart.actions.abandonCart,
  });
};

export const sendCartToExternalSystem = (cart: Cart) => async () => {
  const placedCart = await getAxiosInstance().post('actions', {
    payload: {
      id: cart.id,
    },
    action: domainModel.cart.actions.sendCartToExternalMarketplace,
  });
  return { ...placedCart, cartState: 'sentToExternalSystem' };
};

export const removeCartRow =
  (cartRow: CartRow, cart: Cart) =>
  async (dispatch: Function, getState: Function) => {
    const { authentication } = getState();

    const cartRowTarget = getPersistedCartTarget(
      authentication,
      cart.orderFullfillmentCenter as OrderFullfillmentCenterReference,
      cart.currency as Currency,
    );

    const cartOrigin = domainModel.cart.getCartOrigin(
      authentication.sessionUser.subdomain,
    );

    const pricedArticle = cartRow.pricedArticle as PricedArticleReference;
    const procurementArticle =
      cartRow.procurementArticle as ProcurementArticleReference;

    const payload = {
      target: cartRowTarget,
      quantity: 0,
      pricedArticle,
      orderUnit: pricedArticle.orderUnit,
      orderFullfillmentCenter: cart.orderFullfillmentCenter,
      cartOrigin,
      procurementArticle,
    } as CartSetCartRowParameters;

    if (authentication.sessionUser.customerSubUnit) {
      payload.customerSubUnit = authentication.sessionUser.customerSubUnit;
    } else {
      payload.supplierCustomer = authentication.sessionUser.supplierCustomer;
    }

    await dispatch({
      type: ACTION_TYPES.PERSISTED_CART_ROW_UPDATED,
      payload,
    });

    keyedDebounceSingleton.debounce({
      queue: 'persistCartRow',
      key: procurementArticle?.id || _.toString(pricedArticle.id),
      delayedFunction: () => {
        getAxiosInstance().post('actions', {
          payload,
          action: 'v1.cart.setCartRow',
        });
      },
      delay: 1000,
      retry: true,
    });
  };

export const resetCart = () => (dispatch: Function, getState: Function) => {
  const { persistedMultiCart, authentication } = getState();
  const carts = Object.values(persistedMultiCart.carts) as Cart[];
  carts.forEach(async cart => {
    const cartTarget = getPersistedCartTarget(
      authentication,
      cart.orderFullfillmentCenter as OrderFullfillmentCenterReference,
      cart.currency as Currency,
    );
    if (cart.cartState === 'building') {
      await getAxiosInstance().post('actions', {
        payload: {
          target: cartTarget,
        },
        action: 'v1.cart.deactivate',
      });
    }
  });
};

export const updateQuantityOnCartRow =
  (
    article: PricedArticleReference,
    quantity: number,
    procurementArticle?: ProcurementArticleReference,
    orderedSKU?: StockKeepingUnit,
  ) =>
  (dispatch: Function, getState: Function) => {
    persistCartRow(
      dispatch,
      getState,
      article,
      quantity,
      procurementArticle,
      orderedSKU,
    );
  };

export const loadPersistedMultiCart =
  (req?: Request) => async (dispatch: Function, getState: Function) => {
    const authentication = getState().authentication as Authentication;
    const { customerSubUnit, supplierCustomer } = authentication.sessionUser;
    try {
      const queryParams = customerSubUnit
        ? {
            customerSubUnit: customerSubUnit?.id,
            cartOrigin: domainModel.cart.getCartOrigin(
              authentication.sessionUser.subdomain,
            ),
            active: true,
          }
        : {
            supplierCustomer: supplierCustomer?.id,
            cartOrigin: domainModel.cart.getCartOrigin(
              authentication.sessionUser.subdomain,
            ),
            active: true,
          };

      const cartReferences = (
        await getAxiosInstance(req).post(`carts/search`, {
          query: queryParams,
        })
      ).data;
      const carts: Cart[] = [];
      await Promise.all(
        cartReferences.rows.map(async (cartReference: CartReference) => {
          const cart = (
            await getAxiosInstance(req).get(`carts/${cartReference.id}`)
          ).data;
          carts.push(cart);
        }),
      );

      await dispatch({
        type: ACTION_TYPES.PERSISTED_CARTS_LOADED,
        payload: {
          carts,
        },
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('error loading persisted carts', error);
    }
  };

export const copyExistingOrderToCart =
  (order: Order) => async (dispatch: Function, getState: Function) => {
    dispatch({
      type: ACTION_TYPES.PERSISTED_CART_COPIED_FROM_ORDER,
    });
    await getAxiosInstance().post('actions', {
      payload: {
        order,
        target: getPersistedCartTarget(
          getState().authentication,
          order.orderFullfillmentCenter as OrderFullfillmentCenterReference,
          order.currency as Currency,
        ),
      },
      action: domainModel.cart.actions.copyExistingOrderToCart,
    });
  };
